import type { InventoryResponse } from '@/types';
import type { OrderSummary } from '@/lib/checkoutClient';
import type { EventCatalog } from '@/hooks/useEventCatalog';

/**
 * Mock data fixtures for the H5 demo (ROUND6 P2).
 *
 * Activated by VITE_USE_MOCK_DATA=true. When ON, hooks (useMyOrders,
 * useEventCatalog, useInventory) short-circuit the real Supabase / edge
 * function calls and return these fixtures, so the UAT demo shows
 * realistic UX even though admin's taquilla.* handlers still return
 * HTTP 501.
 *
 * Mock UUIDs are deterministic so deep-linking works across rebuilds.
 */

const EVENT_A_ID = 'demo-event-1111-aaaa-aaaaaaaaaaaa';
const EVENT_B_ID = 'demo-event-2222-bbbb-bbbbbbbbbbbb';

const STAGE_A1 = 'stage-a1-1111-1111-111111111111';
const STAGE_B1 = 'stage-b1-1111-1111-111111111111';

const LOC_A_PALCO = 'loc-aaaa-palco-1111-111111111111';
const LOC_A_VIP = 'loc-aaaa-vip-2222-222222222222';
const LOC_A_GEN = 'loc-aaaa-gen-3333-333333333333';
const LOC_B_PLATINUM = 'loc-bbbb-platinum-1111-111111111111';
const LOC_B_GOLD = 'loc-bbbb-gold-2222-222222222222';

export const MOCK_EVENT_IDS = [EVENT_A_ID, EVENT_B_ID] as const;

export function isMockMode(): boolean {
  return import.meta.env.VITE_USE_MOCK_DATA === 'true';
}

export function getMockEventCatalog(eventId: string): EventCatalog | null {
  if (eventId === EVENT_A_ID) {
    return {
      event: {
        id: EVENT_A_ID,
        title: 'Demo Night · Maluma Live',
        description:
          'Concierto exclusivo en el Estadio El Campín. Experiencia VIP completa con catering premium y acceso prioritario.',
        date: '2026-06-15T21:00:00-05:00',
        venue: 'Estadio El Campín · Bogotá',
        cityName: 'Bogotá',
        categoryName: 'Conciertos',
        status: 'active',
        coverImage: null,
        brandId: 'demo-provider-1111-2222-333333333333',
        currency: 'COP',
        translations: null,
      },
      localities: [
        {
          id: LOC_A_PALCO,
          stageId: STAGE_A1,
          name: 'PALCO PREMIUM',
          priceFullCents: 850_000,
          pricePromoCents: null,
        },
        {
          id: LOC_A_VIP,
          stageId: STAGE_A1,
          name: 'VIP',
          priceFullCents: 450_000,
          pricePromoCents: null,
        },
        {
          id: LOC_A_GEN,
          stageId: STAGE_A1,
          name: 'GENERAL',
          priceFullCents: 180_000,
          pricePromoCents: null,
        },
      ],
    };
  }
  if (eventId === EVENT_B_ID) {
    return {
      event: {
        id: EVENT_B_ID,
        title: 'Karol G · Mañana Será Bonito Tour',
        description:
          'Una noche inolvidable. Tarima principal + experiencias exclusivas para suscriptores First Class.',
        date: '2026-07-22T20:30:00-05:00',
        venue: 'Movistar Arena · Bogotá',
        cityName: 'Bogotá',
        categoryName: 'Conciertos',
        status: 'active',
        coverImage: null,
        brandId: 'demo-provider-1111-2222-333333333333',
        currency: 'COP',
        translations: null,
      },
      localities: [
        {
          id: LOC_B_PLATINUM,
          stageId: STAGE_B1,
          name: 'PLATINUM',
          priceFullCents: 1_200_000,
          pricePromoCents: 1_050_000,
        },
        {
          id: LOC_B_GOLD,
          stageId: STAGE_B1,
          name: 'GOLD',
          priceFullCents: 650_000,
          pricePromoCents: null,
        },
      ],
    };
  }
  return null;
}

export function getMockInventory(eventId: string): InventoryResponse | null {
  const nowIso = new Date().toISOString();
  if (eventId === EVENT_A_ID) {
    return {
      eventId: EVENT_A_ID,
      stages: [
        {
          stageId: STAGE_A1,
          stageName: 'Etapa principal',
          capacity: 500,
          sold: 187,
          reserved: 12,
          available: 301,
          soldPct: 37.4,
          localities: [
            {
              localityId: LOC_A_PALCO,
              name: 'PALCO PREMIUM',
              capacity: 20,
              sold: 14,
              reserved: 1,
              available: 5,
              priceFullCents: 850_000,
              pricePromoCents: null,
            },
            {
              localityId: LOC_A_VIP,
              name: 'VIP',
              capacity: 80,
              sold: 41,
              reserved: 5,
              available: 34,
              priceFullCents: 450_000,
              pricePromoCents: null,
            },
            {
              localityId: LOC_A_GEN,
              name: 'GENERAL',
              capacity: 400,
              sold: 132,
              reserved: 6,
              available: 262,
              priceFullCents: 180_000,
              pricePromoCents: null,
            },
          ],
        },
      ],
      snapshotAt: nowIso,
    };
  }
  if (eventId === EVENT_B_ID) {
    return {
      eventId: EVENT_B_ID,
      stages: [
        {
          stageId: STAGE_B1,
          stageName: 'Sala Karol G',
          capacity: 250,
          sold: 198,
          reserved: 8,
          available: 44,
          soldPct: 79.2,
          localities: [
            {
              localityId: LOC_B_PLATINUM,
              name: 'PLATINUM',
              capacity: 30,
              sold: 28,
              reserved: 1,
              available: 1,
              priceFullCents: 1_200_000,
              pricePromoCents: 1_050_000,
            },
            {
              localityId: LOC_B_GOLD,
              name: 'GOLD',
              capacity: 220,
              sold: 170,
              reserved: 7,
              available: 43,
              priceFullCents: 650_000,
              pricePromoCents: null,
            },
          ],
        },
      ],
      snapshotAt: nowIso,
    };
  }
  return null;
}

function mockQr(
  orderId: string,
  idx: number,
  expiresInDays = 60,
): {
  id: string;
  orderItemId: string;
  code: string;
  expiresAt: string;
} {
  return {
    id: `qr-${orderId.slice(0, 8)}-${idx}`,
    orderItemId: `item-${orderId.slice(0, 8)}-${idx}`,
    code: `${orderId.slice(0, 8).toUpperCase()}-QR-${String(idx).padStart(3, '0')}`,
    expiresAt: new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString(),
  };
}

/** Mock-mode lookup por orderId — usado por useOrderPolling para evitar
 * hits a /taquilla/order/:id (que devuelven 404 contra stubs admin y
 * generan console errors). Devuelve null si no matchea ninguna fixture. */
export function getMockOrder(orderId: string): OrderSummary | null {
  return getMockMyOrders().find((o) => o.id === orderId) ?? null;
}

export function getMockMyOrders(): OrderSummary[] {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  const O1 = 'order-1111-confirmed-2222-aaaaaaaaaaaa';
  const O2 = 'order-2222-qrissued-3333-bbbbbbbbbbbb';
  const O3 = 'order-3333-used-4444-cccccccccccc';
  const O4 = 'order-4444-cancelled-5555-dddddddddddd';
  const O5 = 'order-5555-confirmed-6666-eeeeeeeeeeee';
  return [
    {
      id: O1,
      orderNumber: 'FC-260513-A1B2C3D4',
      status: 'confirmed',
      totalCents: 900_000,
      currency: 'COP',
      items: [
        {
          id: 'item-1-1',
          itemType: 'seat',
          stageId: STAGE_A1,
          localityId: LOC_A_VIP,
          seatIdentifier: 'VIP-12',
          quantity: 2,
          unitPriceCents: 450_000,
        },
      ],
      qrCodes: [mockQr(O1, 1), mockQr(O1, 2)],
      createdAt: new Date(now - 2 * day).toISOString(),
      confirmedAt: new Date(now - 2 * day + 60_000).toISOString(),
    },
    {
      id: O2,
      orderNumber: 'FC-260511-E5F6G7H8',
      status: 'qr_issued',
      totalCents: 180_000,
      currency: 'COP',
      items: [
        {
          id: 'item-2-1',
          itemType: 'general_admission',
          stageId: STAGE_A1,
          localityId: LOC_A_GEN,
          seatIdentifier: null,
          quantity: 1,
          unitPriceCents: 180_000,
        },
      ],
      qrCodes: [mockQr(O2, 1)],
      createdAt: new Date(now - 5 * day).toISOString(),
      confirmedAt: new Date(now - 5 * day + 90_000).toISOString(),
    },
    {
      id: O3,
      orderNumber: 'FC-260420-I9J0K1L2',
      status: 'used',
      totalCents: 850_000,
      currency: 'COP',
      items: [
        {
          id: 'item-3-1',
          itemType: 'box',
          stageId: STAGE_A1,
          localityId: LOC_A_PALCO,
          seatIdentifier: 'PALCO-A-3',
          quantity: 1,
          unitPriceCents: 850_000,
        },
      ],
      qrCodes: [mockQr(O3, 1, 1)], // expired-ish QR for "used" tickets
      createdAt: new Date(now - 30 * day).toISOString(),
      confirmedAt: new Date(now - 30 * day + 120_000).toISOString(),
    },
    {
      id: O4,
      orderNumber: 'FC-260505-M3N4O5P6',
      status: 'cancelled',
      totalCents: 1_200_000,
      currency: 'COP',
      items: [
        {
          id: 'item-4-1',
          itemType: 'seat',
          stageId: STAGE_B1,
          localityId: LOC_B_PLATINUM,
          seatIdentifier: 'PLAT-2',
          quantity: 1,
          unitPriceCents: 1_200_000,
        },
      ],
      qrCodes: [],
      createdAt: new Date(now - 8 * day).toISOString(),
      confirmedAt: null,
    },
    {
      id: O5,
      orderNumber: 'FC-260514-Q7R8S9T0',
      status: 'confirmed',
      totalCents: 650_000,
      currency: 'COP',
      items: [
        {
          id: 'item-5-1',
          itemType: 'seat',
          stageId: STAGE_B1,
          localityId: LOC_B_GOLD,
          seatIdentifier: 'GOLD-B-15',
          quantity: 1,
          unitPriceCents: 650_000,
        },
      ],
      qrCodes: [mockQr(O5, 1)],
      createdAt: new Date(now - 1 * day).toISOString(),
      confirmedAt: new Date(now - 1 * day + 75_000).toISOString(),
    },
  ];
}

// =============================================================================
// ROUND7 P1 — Partners store (Tienda)
// =============================================================================

export type MockPartnerCategory = {
  slug: string;
  name: string;
  description: string;
};

export const MOCK_PARTNER_CATEGORIES: ReadonlyArray<MockPartnerCategory> = [
  {
    slug: 'gastronomia',
    name: 'Gastronomía',
    description: 'Cenas premium, brunches VIP y catas exclusivas.',
  },
  {
    slug: 'retail',
    name: 'Retail',
    description: 'Productos firmados, kits y merchandising oficial.',
  },
  {
    slug: 'lifestyle',
    name: 'Lifestyle',
    description: 'Bienestar, spa, sesiones de foto y experiencias personales.',
  },
  {
    slug: 'servicios',
    name: 'Servicios',
    description: 'Asistencia VIP: chofer, traductor, concierge.',
  },
];

export type MockPartnerProduct = {
  id: string;
  partnerId: string;
  partnerName: string;
  title: string;
  description: string;
  priceCents: number;
  category: string; // slug
  stock: number;
  imageUrl: string | null;
};

const MOCK_PARTNERS = {
  'partner-bistro': 'Bistró Capital',
  'partner-roof': 'Sky Rooftop',
  'partner-vino': 'Casa Vino',
  'partner-shop': 'FC Shop',
  'partner-spa': 'Spa Mandala',
  'partner-photo': 'Studio Foto',
  'partner-driver': 'VIP Driver',
  'partner-concierge': 'Concierge 24',
} as const;

export const MOCK_PARTNER_PRODUCTS: ReadonlyArray<MockPartnerProduct> = [
  // Gastronomía
  {
    id: 'prod-001',
    partnerId: 'partner-bistro',
    partnerName: MOCK_PARTNERS['partner-bistro'],
    title: 'Cena Premium 5 tiempos',
    description: 'Menú degustación con maridaje incluido para 2 personas.',
    priceCents: 320_000,
    category: 'gastronomia',
    stock: 25,
    imageUrl: null,
  },
  {
    id: 'prod-002',
    partnerId: 'partner-roof',
    partnerName: MOCK_PARTNERS['partner-roof'],
    title: 'Brunch VIP terraza',
    description: 'Brunch del domingo con vista panorámica + barra libre 2h.',
    priceCents: 180_000,
    category: 'gastronomia',
    stock: 40,
    imageUrl: null,
  },
  {
    id: 'prod-003',
    partnerId: 'partner-vino',
    partnerName: MOCK_PARTNERS['partner-vino'],
    title: 'Cata de vinos guiada',
    description: 'Sesión de cata con sommelier — 6 etiquetas chilenas y argentinas.',
    priceCents: 240_000,
    category: 'gastronomia',
    stock: 15,
    imageUrl: null,
  },
  {
    id: 'prod-004',
    partnerId: 'partner-bistro',
    partnerName: MOCK_PARTNERS['partner-bistro'],
    title: 'Master class del chef',
    description: 'Clase práctica de cocina con el chef ejecutivo. 3 horas + recetario.',
    priceCents: 280_000,
    category: 'gastronomia',
    stock: 10,
    imageUrl: null,
  },
  // Retail
  {
    id: 'prod-005',
    partnerId: 'partner-shop',
    partnerName: MOCK_PARTNERS['partner-shop'],
    title: 'Kit cóctel artesanal',
    description: 'Set de utensilios premium + 3 botellas premium para coctelería en casa.',
    priceCents: 450_000,
    category: 'retail',
    stock: 50,
    imageUrl: null,
  },
  {
    id: 'prod-006',
    partnerId: 'partner-shop',
    partnerName: MOCK_PARTNERS['partner-shop'],
    title: 'Libro firmado: detrás del show',
    description: 'Edición limitada autografiada. Tapa dura, 240 páginas.',
    priceCents: 95_000,
    category: 'retail',
    stock: 100,
    imageUrl: null,
  },
  {
    id: 'prod-007',
    partnerId: 'partner-shop',
    partnerName: MOCK_PARTNERS['partner-shop'],
    title: 'Hoodie FC oversize',
    description: 'Línea First Class — algodón orgánico 100%, talla S a XL.',
    priceCents: 220_000,
    category: 'retail',
    stock: 80,
    imageUrl: null,
  },
  // Lifestyle
  {
    id: 'prod-008',
    partnerId: 'partner-spa',
    partnerName: MOCK_PARTNERS['partner-spa'],
    title: 'Spa day completo',
    description: 'Día de spa: circuito termal + masaje 60 min + facial + lunch saludable.',
    priceCents: 380_000,
    category: 'lifestyle',
    stock: 20,
    imageUrl: null,
  },
  {
    id: 'prod-009',
    partnerId: 'partner-photo',
    partnerName: MOCK_PARTNERS['partner-photo'],
    title: 'Sesión de fotos premium',
    description: 'Producción 2h + 25 fotos editadas + 5 impresiones tamaño 30x45.',
    priceCents: 520_000,
    category: 'lifestyle',
    stock: 12,
    imageUrl: null,
  },
  {
    id: 'prod-010',
    partnerId: 'partner-spa',
    partnerName: MOCK_PARTNERS['partner-spa'],
    title: 'Masaje deportivo 90 min',
    description: 'Sesión profunda de descarga muscular con terapeuta certificada.',
    priceCents: 150_000,
    category: 'lifestyle',
    stock: 30,
    imageUrl: null,
  },
  // Servicios
  {
    id: 'prod-011',
    partnerId: 'partner-driver',
    partnerName: MOCK_PARTNERS['partner-driver'],
    title: 'Chofer VIP por noche',
    description: 'Vehículo premium con conductor profesional. 6 horas, dentro de ciudad.',
    priceCents: 480_000,
    category: 'servicios',
    stock: 8,
    imageUrl: null,
  },
  {
    id: 'prod-012',
    partnerId: 'partner-concierge',
    partnerName: MOCK_PARTNERS['partner-concierge'],
    title: 'Concierge 24 horas',
    description: 'Asistencia personal: reservas, traducciones, gestiones — 24h continuas.',
    priceCents: 360_000,
    category: 'servicios',
    stock: 5,
    imageUrl: null,
  },
];

export function getMockPartnerProducts(): MockPartnerProduct[] {
  return [...MOCK_PARTNER_PRODUCTS];
}

export function getMockPartnerProduct(id: string): MockPartnerProduct | null {
  return MOCK_PARTNER_PRODUCTS.find((p) => p.id === id) ?? null;
}

export function getMockPartnerProductsByCategory(slug: string): MockPartnerProduct[] {
  return MOCK_PARTNER_PRODUCTS.filter((p) => p.category === slug);
}

export function getMockPartnerCategory(slug: string): MockPartnerCategory | null {
  return MOCK_PARTNER_CATEGORIES.find((c) => c.slug === slug) ?? null;
}

// =============================================================================
// ROUND7 P2 — Memberships
// =============================================================================

export type MockMembershipTier = {
  slug: string;
  name: string;
  shortDescription: string;
  monthlyPriceCents: number | null; // null for B2B "Cotizar"
  annualPriceCents: number | null;
  features: ReadonlyArray<string>;
  benefitsLong: ReadonlyArray<string>;
  targetAudience: string;
  ctaLabel: string;
  tone: 'neutral' | 'success' | 'info' | 'gold';
  recommended: boolean;
};

export const MOCK_MEMBERSHIP_TIERS: ReadonlyArray<MockMembershipTier> = [
  {
    slug: 'standard',
    name: 'Standard',
    shortDescription: 'Acceso al ecosistema First Class con beneficios esenciales.',
    monthlyPriceCents: 60_000,
    annualPriceCents: 600_000,
    features: [
      'Acceso a eventos abiertos',
      '5% descuento en tienda partners',
      'Newsletter mensual con experiencias seleccionadas',
      'Soporte por email',
    ],
    benefitsLong: [
      'Entrada prioritaria a 4 eventos por trimestre.',
      'Acceso a flash sales con 72h de anticipación.',
      'Programa de puntos básico (1 punto por cada $1.000 COP).',
    ],
    targetAudience: 'Ideal si estás conociendo First Class.',
    ctaLabel: 'Suscribirme',
    tone: 'neutral',
    recommended: false,
  },
  {
    slug: 'premium',
    name: 'Premium',
    shortDescription: 'Beneficios elevados + experiencias VIP curadas.',
    monthlyPriceCents: 180_000,
    annualPriceCents: 1_800_000,
    features: [
      'Todo lo del plan Standard',
      'Reserva prioritaria en eventos VIP',
      '15% descuento en tienda partners',
      'Acceso anticipado 7 días a flash sales',
      'Concierge digital 12 horas',
    ],
    benefitsLong: [
      '2 experiencias VIP de regalo por año (cena con chef, viaje express).',
      'Programa de puntos x2.',
      'Membresía transferible una vez al año a un acompañante.',
    ],
    targetAudience: 'Para quienes viven la marca con frecuencia.',
    ctaLabel: 'Suscribirme',
    tone: 'gold',
    recommended: true,
  },
  {
    slug: 'full',
    name: 'Full',
    shortDescription: 'Experiencia sin fricción — todo desbloqueado.',
    monthlyPriceCents: 420_000,
    annualPriceCents: 4_200_000,
    features: [
      'Todo lo del plan Premium',
      'Reserva sin lista de espera en cualquier evento',
      '30% descuento en tienda partners',
      'Concierge 24 horas',
      'Invitaciones a eventos privados solo Full',
      'Asesor personal de experiencias',
    ],
    benefitsLong: [
      '6 experiencias VIP de regalo por año.',
      'Programa de puntos x4 + bonificación mensual.',
      'Cupos garantizados para grupos pequeños en cenas privadas.',
      'Tracking dedicado de tus eventos favoritos por temporada.',
    ],
    targetAudience: 'La experiencia completa, sin compromisos.',
    ctaLabel: 'Suscribirme',
    tone: 'success',
    recommended: false,
  },
  {
    slug: 'b2b-visitor',
    name: 'B2B Visitor',
    shortDescription: 'Plan corporativo para empresas que reciben visitantes.',
    monthlyPriceCents: null,
    annualPriceCents: null,
    features: [
      'Acceso temporal para visitantes corporativos',
      'Tarjetas digitales emitidas por evento',
      'Facturación mensual consolidada',
      'Account manager dedicado',
    ],
    benefitsLong: [
      'Hasta 50 pases mensuales activos por contrato.',
      'Reportería de uso + analítica de adopción.',
      'Onboarding asistido para tu equipo de hospitality.',
    ],
    targetAudience:
      'Compañías que quieran ofrecer First Class como beneficio a clientes o talento.',
    ctaLabel: 'Cotizar B2B',
    tone: 'info',
    recommended: false,
  },
];

export function getMockMembershipTiers(): MockMembershipTier[] {
  return [...MOCK_MEMBERSHIP_TIERS];
}

export function getMockMembershipTier(slug: string): MockMembershipTier | null {
  return MOCK_MEMBERSHIP_TIERS.find((t) => t.slug === slug) ?? null;
}

// =============================================================================
// ROUND7 P3 — Experiencias VIP
// =============================================================================

export type MockExperienceCategory = 'cena' | 'viaje' | 'encuentro';

export type MockExperience = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  category: MockExperienceCategory;
  location: string;
  duration: string;
  capacity: number;
  priceCents: number;
  imageEmoji: string;
  highlights: ReadonlyArray<string>;
};

export const MOCK_EXPERIENCES: ReadonlyArray<MockExperience> = [
  {
    id: 'exp-001',
    title: 'Cena privada con chef estrella',
    subtitle: '8 tiempos · mesa para 4',
    description:
      'Una cena íntima con el chef ejecutivo del Bistró Capital. Maridaje con sommelier, menú estacional, conversación abierta con el chef entre tiempos.',
    category: 'cena',
    location: 'Bistró Capital · Bogotá',
    duration: '4 horas',
    capacity: 4,
    priceCents: 2_400_000,
    imageEmoji: '🍷',
    highlights: ['Menú degustación 8 tiempos', 'Maridaje incluido', 'Recetario de regalo'],
  },
  {
    id: 'exp-002',
    title: 'Chef en tu casa',
    subtitle: 'Cena premium para 6 personas',
    description:
      'Equipo de chef + sous chef + sommelier llegan a tu casa con ingredientes premium. Tres tiempos, postre y limpieza incluida.',
    category: 'cena',
    location: 'En tu hogar · Bogotá área metro',
    duration: '5 horas',
    capacity: 6,
    priceCents: 1_800_000,
    imageEmoji: '🍽️',
    highlights: ['Logística end-to-end', 'Maridaje 4 etiquetas', 'Postre privado'],
  },
  {
    id: 'exp-003',
    title: 'Escapada de fin de semana · Cartagena',
    subtitle: '3 noches · hotel boutique + city tour VIP',
    description:
      'Vuelo + hotel boutique en Centro Histórico + city tour privado en convertible + cena de bienvenida y cierre. Concierge dedicado todo el viaje.',
    category: 'viaje',
    location: 'Cartagena de Indias',
    duration: '3 noches',
    capacity: 2,
    priceCents: 6_500_000,
    imageEmoji: '🏖️',
    highlights: ['Vuelos incluidos', 'Hotel 5-estrellas', 'Tour privado en convertible'],
  },
  {
    id: 'exp-004',
    title: 'Ruta del café en Salento',
    subtitle: '2 noches · finca exclusiva + cata',
    description:
      'Recorrido completo: vuelo, transfer privado, alojamiento en finca cafetera, cata de cafés especiales con catador certificado, masaje + spa al cierre.',
    category: 'viaje',
    location: 'Eje Cafetero',
    duration: '2 noches',
    capacity: 2,
    priceCents: 3_900_000,
    imageEmoji: '☕',
    highlights: ['Finca exclusiva', 'Cata profesional', 'Spa de cierre'],
  },
  {
    id: 'exp-005',
    title: 'Encuentro privado con artista del momento',
    subtitle: 'Meet & greet + cena de cierre',
    description:
      'Acceso backstage + meet & greet 30 min + cena en privado tras el show con el artista (cuando agenda lo permite). Para grupos pequeños.',
    category: 'encuentro',
    location: 'Movistar Arena · Bogotá',
    duration: '6 horas',
    capacity: 6,
    priceCents: 5_200_000,
    imageEmoji: '🎤',
    highlights: ['Acceso backstage', 'Meet & greet con artista', 'Cena privada post-show'],
  },
  {
    id: 'exp-006',
    title: 'Round table empresarial con CEO',
    subtitle: 'Networking de alto nivel · 8 plazas',
    description:
      'Mesa redonda mensual con CEO de empresa líder + grupo curado de 8 participantes. Tema técnico o estratégico, off-the-record. Whisky tasting incluido.',
    category: 'encuentro',
    location: 'Sky Rooftop · Bogotá',
    duration: '3 horas',
    capacity: 8,
    priceCents: 850_000,
    imageEmoji: '🥃',
    highlights: ['Off-the-record', 'Cata de whisky', 'Networking curado'],
  },
];

export function getMockExperiences(): MockExperience[] {
  return [...MOCK_EXPERIENCES];
}

export function getMockExperience(id: string): MockExperience | null {
  return MOCK_EXPERIENCES.find((e) => e.id === id) ?? null;
}

export function getMockExperiencesByCategory(cat: MockExperienceCategory): MockExperience[] {
  return MOCK_EXPERIENCES.filter((e) => e.category === cat);
}

// =============================================================================
// ROUND7 P4 — User Account hub
// =============================================================================

export type MockPaymentMethod = {
  id: string;
  brand: 'visa' | 'mastercard' | 'amex';
  last4: string;
  expMonth: number;
  expYear: number;
  cardholderName: string;
  isDefault: boolean;
};

export type MockNotificationPrefs = {
  emailMarketing: boolean;
  emailTransactional: boolean;
  smsReminders: boolean;
  pushApp: boolean;
};

export type MockUserProfile = {
  fullName: string;
  email: string;
  avatarUrl: string | null;
  initials: string;
  tierSlug: string;
  tierName: string;
  tierExpiresAt: string;
  renewalAmountCents: number;
  paymentMethods: ReadonlyArray<MockPaymentMethod>;
  notificationPrefs: MockNotificationPrefs;
  memberSince: string;
};

export const MOCK_USER_PROFILE: MockUserProfile = {
  fullName: 'Alejandro Cortés',
  email: 'full@firstclass.com',
  avatarUrl: null,
  initials: 'AC',
  tierSlug: 'premium',
  tierName: 'Premium',
  tierExpiresAt: '2027-05-15T00:00:00-05:00',
  renewalAmountCents: 180_000,
  paymentMethods: [
    {
      id: 'pm-001',
      brand: 'visa',
      last4: '4242',
      expMonth: 12,
      expYear: 2028,
      cardholderName: 'Alejandro Cortés',
      isDefault: true,
    },
    {
      id: 'pm-002',
      brand: 'mastercard',
      last4: '5454',
      expMonth: 8,
      expYear: 2027,
      cardholderName: 'Alejandro Cortés',
      isDefault: false,
    },
  ],
  notificationPrefs: {
    emailMarketing: true,
    emailTransactional: true,
    smsReminders: false,
    pushApp: true,
  },
  memberSince: '2024-08-15T00:00:00-05:00',
};

export function getMockUserProfile(): MockUserProfile {
  return MOCK_USER_PROFILE;
}
